import _ from "lodash";
import React, { useState, useCallback } from "react";
import { useI18n } from "Hooks";

import { getIsChina } from "config";
import ACLStore from "lib/aclStore";

// design system
import {
  Avatar,
  Button,
  Dropdown,
  DropdownMenu,
  IconButton,
  Tooltip,
} from "@toddle-edu/ds-web";
import { SetupOutlined } from "@toddle-edu/ds-icons";
import { useAIChatContext } from "UIComponents/AIChat/context/AIChatContext";

// constants
import { AI_TUTOR_LEARNING_TOOLS as LEARNING_TOOLS } from "@toddle-edu/common-utils";
import { getLearningToolIcon } from "Utils/AIUtils";
import { getIsReactPlayerVideoModalityEnabled } from "Utils/ACLStoreUtils";

const styles = {
  overlayStyle: {
    minWidth: "240px",
  },
};

const LearningToolsDropdown = props => {
  const {
    onToolSelected = _.noop,
    testId = "chatFooter-learningToolsDropdown",
    disabled = false,
    tooltipLabel,
  } = props;

  const { t } = useI18n();
  const { selectedModalityId } = useAIChatContext();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleToolClick = useCallback(
    ({ key }) => {
      const selectedTool = _.find(LEARNING_TOOLS, { key });
      if (selectedTool) {
        onToolSelected(selectedTool);
        setIsDropdownOpen(false);
      }
    },
    [onToolSelected]
  );

  const getFilteredLearningTools = () =>
    _.filter(LEARNING_TOOLS, tool => {
      if (
        getIsChina() &&
        !getIsReactPlayerVideoModalityEnabled() &&
        tool?.key === "video_explorer"
      ) {
        return false;
      }

      return !tool.perm || ACLStore.can(tool.perm);
    });

  const _LEARNING_TOOLS = getFilteredLearningTools();

  if (_.isEmpty(_LEARNING_TOOLS)) {
    return null;
  }

  const dropdownOptions = _.map(_LEARNING_TOOLS, tool => {
    const { key, type, label, subText, avatarColor } = tool;

    const { outlinedIcon } = getLearningToolIcon({ key });

    return {
      key,
      type,
      label: t(label),
      subText: t(subText),
      icon: (
        <Avatar
          icon={outlinedIcon}
          size={"small"}
          iconType={"icon"}
          shape={"square"}
          color={avatarColor}
        />
      ),
    };
  });

  const commonProps = {
    id: "learning-tools",
    icon: <SetupOutlined />,
    testId: `${testId}-button`,
    size: "medium",
    disabled,
    isActivated: isDropdownOpen,
    dsVersion: "2.0",
  };

  const renderButton = () => {
    if (selectedModalityId) {
      return (
        <IconButton
          {...commonProps}
          variant={"neutral"}
          type={"plain"}
          label={tooltipLabel}
        />
      );
    }

    return (
      <Button {...commonProps} variant={"neutral"} type={"plain"}>
        {tooltipLabel}
      </Button>
    );
  };

  return (
    <Dropdown
      overlay={
        <DropdownMenu
          options={dropdownOptions}
          onClick={handleToolClick}
          shouldStopPropagation={true}
        />
      }
      disabled={disabled}
      placement={"bottomLeft"}
      overlayStyle={styles.overlayStyle}
      visible={isDropdownOpen}
      onVisibleChange={setIsDropdownOpen}
    >
      {/* dropdown is not getting triggered after state update when tooltip is the parent element so wrapped it inside a div */}
      <div>
        <Tooltip tooltip={tooltipLabel} showArrow={true}>
          {renderButton()}
        </Tooltip>
      </div>
    </Dropdown>
  );
};

export default LearningToolsDropdown;
